// Groups UI Component
import {
    getAllGroups, getUserGroups, createGroup, joinGroup, leaveGroup,
    joinGroupWithCode, isGroupMember, createGroupPost, deleteGroupPost,
    getUserById, uploadGroupIcon,
} from '../db.js';
import { getCurrentUser } from '../auth.js';
import { db } from '../firebase.js';
import {
    collection, query, orderBy, limit, onSnapshot, doc, getDoc,
} from 'firebase/firestore';
import { getVerse, getVerses } from '../bible.js';
import { showVerseSelector } from './verseSelector.js';

let groupsContainer = null;
let currentGroupId = null;
let groupFeedUnsubscribe = null;

// ─────────────────────────────────────────
// Init
// ─────────────────────────────────────────

export function initGroups(container) {
    groupsContainer = container;
    renderGroupsList();
}

// ─────────────────────────────────────────
// Groups List View
// ─────────────────────────────────────────

async function renderGroupsList() {
    if (!groupsContainer) return;
    cleanupGroupFeed();
    currentGroupId = null;

    groupsContainer.innerHTML = `
        <div class="groups-header">
            <h2>👥 Groupes</h2>
            <button class="btn btn-primary" id="create-group-btn">+ Créer un groupe</button>
        </div>
        <div class="groups-tabs">
            <button class="group-tab active" data-gtab="my">Mes groupes</button>
            <button class="group-tab" data-gtab="discover">Découvrir</button>
        </div>
        <div class="groups-join-code">
            <input type="text" id="invite-code-input" placeholder="Entrer un code d'invitation..." maxlength="6" />
            <button class="btn btn-secondary" id="join-code-btn">Rejoindre</button>
        </div>
        <div id="groups-list-content" class="groups-list">
            <div class="loading">⏳ Chargement des groupes...</div>
        </div>
    `;

    // Create group button
    groupsContainer.querySelector('#create-group-btn').addEventListener('click', showCreateGroupModal);

    // Tab switching
    groupsContainer.querySelectorAll('.group-tab').forEach(tab => {
        tab.addEventListener('click', () => {
            groupsContainer.querySelectorAll('.group-tab').forEach(t => t.classList.remove('active'));
            tab.classList.add('active');
            loadGroupsTab(tab.dataset.gtab);
        });
    });

    // Join with code
    groupsContainer.querySelector('#join-code-btn').addEventListener('click', handleJoinWithCode);
    groupsContainer.querySelector('#invite-code-input').addEventListener('keydown', (e) => {
        if (e.key === 'Enter') handleJoinWithCode();
    });

    loadGroupsTab('my');
}

async function loadGroupsTab(tab) {
    const listEl = groupsContainer.querySelector('#groups-list-content');
    listEl.innerHTML = '<div class="loading">⏳ Chargement...</div>';

    const currentUser = getCurrentUser();

    try {
        let groups = [];
        if (tab === 'my') {
            if (!currentUser) {
                listEl.innerHTML = '<div class="empty-state"><p>Connectez-vous pour voir vos groupes.</p></div>';
                return;
            }
            groups = await getUserGroups(currentUser.uid);
        } else {
            groups = await getAllGroups();
            // Filter: show all, but mark membership
            if (currentUser) {
                const memberChecks = await Promise.all(groups.map(g => isGroupMember(g.id, currentUser.uid)));
                groups = groups.map((g, i) => ({ ...g, isMember: memberChecks[i] }));
            }
        }

        if (groups.length === 0) {
            listEl.innerHTML = `
                <div class="empty-state">
                    <div class="empty-icon">👥</div>
                    <p>${tab === 'my' ? 'Vous ne faites partie d\'aucun groupe.' : 'Aucun groupe disponible.'}</p>
                    ${tab === 'my' ? '<p>Créez un groupe ou rejoignez-en un !</p>' : ''}
                </div>
            `;
            return;
        }

        listEl.innerHTML = '';
        for (const group of groups) {
            const card = buildGroupCard(group, tab, currentUser);
            listEl.appendChild(card);
        }
    } catch (err) {
        console.error('Error loading groups:', err);
        listEl.innerHTML = `<div class="error-state"><p>Erreur : ${err.message}</p></div>`;
    }
}

function buildGroupCard(group, tab, currentUser) {
    const card = document.createElement('div');
    card.className = 'group-card';

    const isMember = group.isMember !== undefined ? group.isMember : true; // in 'my' tab all are members
    const isAdmin = currentUser && group.createdBy === currentUser.uid;
    const isPrivate = group.visibility === 'private';

    let actionBtn = '';
    if (isMember) {
        actionBtn = `<button class="btn btn-group-enter" data-action="enter">Entrer →</button>`;
    } else if (isPrivate) {
        actionBtn = `<span class="group-private-badge">🔒 Privé</span>`;
    } else {
        actionBtn = `<button class="btn btn-group-join" data-action="join">Rejoindre</button>`;
    }

    card.innerHTML = `
        <div class="group-card-emoji">${group.iconURL ? `<img src="${group.iconURL}" class="group-icon-img" alt="${escapeHtml(group.name)}">` : (group.emoji || '📖')}</div>
        <div class="group-card-body">
            <div class="group-card-name">${escapeHtml(group.name)}</div>
            <div class="group-card-desc">${escapeHtml(group.description || '')}</div>
            <div class="group-card-meta">
                <span>👤 ${group.membersCount || 1} membre${(group.membersCount || 1) > 1 ? 's' : ''}</span>
                ${isPrivate ? '<span class="group-badge-private">🔒 Privé</span>' : '<span class="group-badge-public">🌐 Public</span>'}
            </div>
        </div>
        <div class="group-card-actions">
            ${actionBtn}
        </div>
    `;

    const enterBtn = card.querySelector('[data-action="enter"]');
    if (enterBtn) enterBtn.addEventListener('click', () => renderGroupDetail(group));

    const joinBtn = card.querySelector('[data-action="join"]');
    if (joinBtn) joinBtn.addEventListener('click', () => handleJoinGroup(group, card));

    return card;
}

async function handleJoinGroup(group, card) {
    const currentUser = getCurrentUser();
    if (!currentUser) {
        showGroupToast('Connectez-vous pour rejoindre un groupe.', 'error');
        return;
    }
    try {
        await joinGroup(group.id, currentUser.uid);
        showGroupToast(`Vous avez rejoint "${group.name}" !`, 'success');
        // Refresh
        loadGroupsTab('discover');
    } catch (err) {
        showGroupToast(err.message, 'error');
    }
}

async function handleJoinWithCode() {
    const currentUser = getCurrentUser();
    if (!currentUser) {
        showGroupToast('Connectez-vous pour rejoindre un groupe.', 'error');
        return;
    }
    const input = groupsContainer.querySelector('#invite-code-input');
    const code = input.value.trim().toUpperCase();
    if (!code) return;

    try {
        const group = await joinGroupWithCode(code, currentUser.uid);
        input.value = '';
        showGroupToast(`Vous avez rejoint "${group.name}" !`, 'success');
        renderGroupDetail(group);
    } catch (err) {
        showGroupToast(err.message, 'error');
    }
}

// ─────────────────────────────────────────
// Create Group Modal
// ─────────────────────────────────────────

function showCreateGroupModal() {
    const currentUser = getCurrentUser();
    if (!currentUser) {
        showGroupToast('Connectez-vous pour créer un groupe.', 'error');
        return;
    }

    const overlay = document.createElement('div');
    overlay.className = 'modal';
    overlay.innerHTML = `
        <div class="modal-content group-create-modal">
            <div class="modal-header">
                <h3>👥 Créer un groupe</h3>
                <button class="close-btn">✕</button>
            </div>
            <div class="modal-body">
                <form id="create-group-form">
                    <div class="form-group">
                        <label>Icône du groupe</label>
                        <div class="group-icon-upload-area" id="group-icon-upload-area">
                            <div class="group-icon-preview" id="group-icon-preview">📖</div>
                            <input type="file" id="group-icon-file" accept="image/*" style="display:none;" />
                            <small class="group-icon-hint">Cliquer pour ajouter une photo (max 5 Mo)</small>
                        </div>
                    </div>
                    <div class="form-group">
                        <label>Nom du groupe *</label>
                        <input type="text" id="group-name" placeholder="Ex: Évangélistes, Jeunes, Psaumes..." required maxlength="60" />
                    </div>
                    <div class="form-group">
                        <label>Description</label>
                        <textarea id="group-desc" placeholder="De quoi parle ce groupe ?" rows="2" maxlength="300"></textarea>
                    </div>
                    <div class="form-group">
                        <label>Visibilité</label>
                        <div class="visibility-options">
                            <label class="visibility-option">
                                <input type="radio" name="visibility" value="public" checked />
                                <span>🌐 Public — tout le monde peut rejoindre</span>
                            </label>
                            <label class="visibility-option">
                                <input type="radio" name="visibility" value="private" />
                                <span>🔒 Privé — sur code d'invitation</span>
                            </label>
                        </div>
                    </div>
                    <div class="group-create-actions">
                        <button type="button" class="btn btn-secondary" id="cancel-create-btn">Annuler</button>
                        <button type="submit" class="btn btn-primary" id="submit-create-btn">Créer le groupe</button>
                    </div>
                </form>
            </div>
        </div>
    `;

    document.body.appendChild(overlay);

    overlay.querySelector('.close-btn').addEventListener('click', () => overlay.remove());
    overlay.querySelector('#cancel-create-btn').addEventListener('click', () => overlay.remove());
    overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });

    // File upload preview
    const iconUploadArea = overlay.querySelector('#group-icon-upload-area');
    const iconPreview = overlay.querySelector('#group-icon-preview');
    const iconFileInput = overlay.querySelector('#group-icon-file');
    let selectedIconFile = null;

    iconUploadArea.addEventListener('click', () => iconFileInput.click());
    iconFileInput.addEventListener('change', (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            showGroupToast('Image trop lourde (max 5 Mo)', 'error');
            iconFileInput.value = '';
            return;
        }
        selectedIconFile = file;
        const reader = new FileReader();
        reader.onload = (ev) => {
            iconPreview.innerHTML = `<img src="${ev.target.result}" alt="Aperçu" class="group-icon-preview-img">`;
        };
        reader.readAsDataURL(file);
    });

    overlay.querySelector('#create-group-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const submitBtn = overlay.querySelector('#submit-create-btn');
        submitBtn.disabled = true;
        submitBtn.textContent = '⏳ Création...';

        const data = {
            name: overlay.querySelector('#group-name').value.trim(),
            description: overlay.querySelector('#group-desc').value.trim(),
            visibility: overlay.querySelector('input[name="visibility"]:checked').value,
        };

        try {
            const groupId = await createGroup(data, currentUser.uid);

            // Upload icon if a file was selected
            if (selectedIconFile) {
                submitBtn.textContent = '📤 Upload de l\'icône...';
                await uploadGroupIcon(groupId, selectedIconFile);
            }

            const groupSnap = await getDoc(doc(db, 'groups', groupId));
            const groupData = { id: groupId, ...groupSnap.data() };
            overlay.remove();

            if (data.visibility === 'private') {
                showInviteCodeModal(groupData);
                renderGroupDetail(groupData);
            } else {
                showGroupToast(`Groupe "${data.name}" créé !`, 'success');
                renderGroupDetail(groupData);
            }
        } catch (err) {
            console.error('Error creating group:', err);
            showGroupToast('Erreur lors de la création.', 'error');
            submitBtn.disabled = false;
            submitBtn.textContent = 'Créer le groupe';
        }
    });
}

function showInviteCodeModal(group) {
    const overlay = document.createElement('div');
    overlay.className = 'modal';
    overlay.innerHTML = `
        <div class="modal-content group-invite-modal">
            <div class="modal-header">
                <h3>🔒 Groupe privé créé !</h3>
                <button class="close-btn">✕</button>
            </div>
            <div class="modal-body">
                <p>Partagez ce code d'invitation avec les membres :</p>
                <div class="invite-code-display">${group.inviteCode}</div>
                <button class="btn btn-secondary" id="copy-code-btn">📋 Copier le code</button>
            </div>
        </div>
    `;
    document.body.appendChild(overlay);
    overlay.querySelector('.close-btn').addEventListener('click', () => overlay.remove());
    overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
    overlay.querySelector('#copy-code-btn').addEventListener('click', () => {
        navigator.clipboard.writeText(group.inviteCode).then(() => {
            showGroupToast('Code copié !', 'success');
        });
    });
}

// ─────────────────────────────────────────
// Group Detail View
// ─────────────────────────────────────────

async function renderGroupDetail(group) {
    if (!groupsContainer) return;
    cleanupGroupFeed();
    currentGroupId = group.id;

    const currentUser = getCurrentUser();
    const isMember = currentUser ? await isGroupMember(group.id, currentUser.uid) : false;
    const isAdmin = currentUser && group.createdBy === currentUser.uid;

    groupsContainer.innerHTML = `
        <div class="group-detail">
            <button class="btn-back" id="back-to-groups">← Retour aux groupes</button>

            <div class="group-detail-header">
                <div class="group-detail-emoji">${group.iconURL ? `<img src="${group.iconURL}" class="group-icon-img group-detail-icon-img" alt="${escapeHtml(group.name)}">` : (group.emoji || '📖')}</div>
                <div class="group-detail-info">
                    <h2>${escapeHtml(group.name)}</h2>
                    <p class="group-detail-desc">${escapeHtml(group.description || '')}</p>
                    <div class="group-detail-meta">
                        <span>👤 ${group.membersCount || 1} membre${(group.membersCount || 1) > 1 ? 's' : ''}</span>
                        ${group.visibility === 'private' ? '<span class="group-badge-private">🔒 Privé</span>' : '<span class="group-badge-public">🌐 Public</span>'}
                    </div>
                </div>
                <div class="group-detail-actions">
                    ${isAdmin && group.visibility === 'private' ? `<button class="btn btn-secondary" id="show-code-btn">🔑 Code</button>` : ''}
                    ${!isMember && !isAdmin && group.visibility === 'public' ? `<button class="btn btn-primary" id="join-detail-btn">Rejoindre</button>` : ''}
                    ${isMember && !isAdmin ? `<button class="btn btn-secondary" id="leave-group-btn">Quitter</button>` : ''}
                </div>
            </div>

            ${isMember ? `
            <div class="group-compose" id="group-compose">
                <form id="group-post-form">
                    <div class="group-compose-verse" id="group-verse-area"></div>
                    <input type="hidden" id="gpost-book" />
                    <input type="hidden" id="gpost-chapter" />
                    <input type="hidden" id="gpost-verse-start" />
                    <input type="hidden" id="gpost-verse-end" />
                    <div class="form-group">
                        <button type="button" class="btn btn-secondary" id="group-verse-btn">📖 Ajouter un verset</button>
                    </div>
                    <div class="form-group">
                        <textarea id="gpost-text" placeholder="Partagez une réflexion avec le groupe..." rows="2" required></textarea>
                    </div>
                    <button type="submit" class="btn btn-primary">Publier dans le groupe</button>
                </form>
            </div>
            ` : ''}

            <div class="group-feed" id="group-feed">
                <div class="loading">⏳ Chargement du fil du groupe...</div>
            </div>
        </div>
    `;

    // Back button
    groupsContainer.querySelector('#back-to-groups').addEventListener('click', renderGroupsList);

    // Show invite code
    const showCodeBtn = groupsContainer.querySelector('#show-code-btn');
    if (showCodeBtn) showCodeBtn.addEventListener('click', () => showInviteCodeModal(group));

    // Join from detail
    const joinDetailBtn = groupsContainer.querySelector('#join-detail-btn');
    if (joinDetailBtn) {
        joinDetailBtn.addEventListener('click', async () => {
            if (!currentUser) { showGroupToast('Connectez-vous.', 'error'); return; }
            try {
                await joinGroup(group.id, currentUser.uid);
                showGroupToast('Vous avez rejoint le groupe !', 'success');
                // Re-render with updated membership
                const snap = await getDoc(doc(db, 'groups', group.id));
                renderGroupDetail({ id: group.id, ...snap.data() });
            } catch (err) { showGroupToast(err.message, 'error'); }
        });
    }

    // Leave group
    const leaveBtn = groupsContainer.querySelector('#leave-group-btn');
    if (leaveBtn) {
        leaveBtn.addEventListener('click', async () => {
            if (!confirm(`Quitter le groupe "${group.name}" ?`)) return;
            try {
                await leaveGroup(group.id, currentUser.uid);
                showGroupToast('Vous avez quitté le groupe.', 'success');
                renderGroupsList();
            } catch (err) { showGroupToast(err.message, 'error'); }
        });
    }

    // Group compose form
    if (isMember) {
        initGroupCompose(group.id);
    }

    // Load group feed with real-time listener
    loadGroupFeed(group.id);
}

// ─────────────────────────────────────────
// Group Compose
// ─────────────────────────────────────────

function initGroupCompose(groupId) {
    const verseBtn = groupsContainer.querySelector('#group-verse-btn');
    const verseArea = groupsContainer.querySelector('#group-verse-area');
    const form = groupsContainer.querySelector('#group-post-form');

    if (!form) return;

    verseBtn.addEventListener('click', () => {
        showVerseSelector(async (verse) => {
            groupsContainer.querySelector('#gpost-book').value = verse.book;
            groupsContainer.querySelector('#gpost-chapter').value = verse.chapter;
            groupsContainer.querySelector('#gpost-verse-start').value = verse.verseStart;
            groupsContainer.querySelector('#gpost-verse-end').value = verse.verseEnd || verse.verseStart;

            const verseRef = verse.verseStart === verse.verseEnd
                ? `${verse.bookName} ${verse.chapter}:${verse.verseStart}`
                : `${verse.bookName} ${verse.chapter}:${verse.verseStart}-${verse.verseEnd}`;

            verseArea.innerHTML = `
                <div class="verse-link-display">
                    <span class="verse-info">📖 ${verseRef}</span>
                    <button type="button" class="remove-verse-btn">×</button>
                </div>
            `;

            verseArea.querySelector('.remove-verse-btn').addEventListener('click', () => {
                groupsContainer.querySelector('#gpost-book').value = '';
                groupsContainer.querySelector('#gpost-chapter').value = '';
                groupsContainer.querySelector('#gpost-verse-start').value = '';
                groupsContainer.querySelector('#gpost-verse-end').value = '';
                verseArea.innerHTML = '';
            });
        });
    });

    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const currentUser = getCurrentUser();
        if (!currentUser) return;

        const text = form.querySelector('#gpost-text').value.trim();
        if (!text) return;

        const submitBtn = form.querySelector('button[type="submit"]');
        submitBtn.disabled = true;
        submitBtn.textContent = '⏳ Publication...';

        try {
            const postData = {
                text,
                language: localStorage.getItem('user_preferred_lang') || 'french',
                book: form.querySelector('#gpost-book').value || null,
                chapter: form.querySelector('#gpost-chapter').value ? parseInt(form.querySelector('#gpost-chapter').value) : null,
                verseStart: form.querySelector('#gpost-verse-start').value ? parseInt(form.querySelector('#gpost-verse-start').value) : null,
                verseEnd: form.querySelector('#gpost-verse-end').value ? parseInt(form.querySelector('#gpost-verse-end').value) : null,
            };
            // Remove null fields
            Object.keys(postData).forEach(k => postData[k] === null && delete postData[k]);

            await createGroupPost(groupId, postData, currentUser.uid);
            form.reset();
            verseArea.innerHTML = '';
            showGroupToast('Publication ajoutée !', 'success');
        } catch (err) {
            console.error('Error posting to group:', err);
            showGroupToast('Erreur lors de la publication.', 'error');
        } finally {
            submitBtn.disabled = false;
            submitBtn.textContent = 'Publier dans le groupe';
        }
    });
}

// ─────────────────────────────────────────
// Group Feed
// ─────────────────────────────────────────

function loadGroupFeed(groupId) {
    const feedEl = groupsContainer.querySelector('#group-feed');
    if (!feedEl) return;

    const q = query(
        collection(db, 'groups', groupId, 'posts'),
        orderBy('createdAt', 'desc'),
        limit(50)
    );

    groupFeedUnsubscribe = onSnapshot(q, async (snapshot) => {
        if (snapshot.empty) {
            feedEl.innerHTML = `
                <div class="empty-state">
                    <div class="empty-icon">📖</div>
                    <p>Aucune publication dans ce groupe.</p>
                    <p>Soyez le premier à partager !</p>
                </div>
            `;
            return;
        }

        feedEl.innerHTML = '';
        for (const docSnap of snapshot.docs) {
            const post = { id: docSnap.id, ...docSnap.data() };
            try {
                post.author = await getUserById(post.authorId);
            } catch {
                post.author = { displayName: 'Utilisateur inconnu', photoURL: '' };
            }
            const card = buildGroupPostCard(post, groupId);
            feedEl.appendChild(card);
        }
    }, (err) => {
        console.error('Group feed error:', err);
        feedEl.innerHTML = `<div class="error-state"><p>Erreur de chargement.</p></div>`;
    });
}

function buildGroupPostCard(post, groupId) {
    const currentUser = getCurrentUser();
    const isAuthor = currentUser && currentUser.uid === post.authorId;

    const postDate = post.createdAt
        ? new Date(post.createdAt.toDate()).toLocaleDateString('fr-FR', {
            year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit',
        })
        : 'Date inconnue';

    let verseRef = '';
    if (post.book && post.chapter) {
        verseRef = `
            <div class="verse-ref">
                📖 ${post.book} ${post.chapter}${post.verseStart ? ':' + post.verseStart : ''}${post.verseEnd && post.verseEnd !== post.verseStart ? '-' + post.verseEnd : ''}
            </div>
        `;
    }

    const card = document.createElement('article');
    card.className = 'post-card group-post-card';
    card.dataset.postId = post.id;
    card.innerHTML = `
        <div class="post-header">
            <img src="${post.author.photoURL || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(post.author.displayName)}"
                alt="${escapeHtml(post.author.displayName)}" class="author-avatar" />
            <div class="author-info">
                <div class="author-name">${escapeHtml(post.author.displayName)}</div>
                <div class="post-date">${postDate}</div>
            </div>
            ${isAuthor ? `
                <div class="post-action-buttons">
                    <button class="delete-btn" data-action="delete" title="Supprimer">🗑️</button>
                </div>
            ` : ''}
        </div>
        ${verseRef}
        <div class="post-content">${escapeHtml(post.text)}</div>
    `;

    // Lazy verse loading
    if (post.book && post.chapter) {
        const verseContainer = document.createElement('div');
        verseContainer.className = 'verse-text-container';
        verseContainer.dataset.loaded = 'false';
        verseContainer.dataset.book = post.book;
        verseContainer.dataset.chapter = post.chapter;
        verseContainer.dataset.verseStart = post.verseStart || '';
        verseContainer.dataset.verseEnd = post.verseEnd || '';
        const verseRefNode = card.querySelector('.verse-ref');
        if (verseRefNode) {
            verseRefNode.insertAdjacentElement('afterend', verseContainer);
            createGroupVerseObserver().observe(verseContainer);
        }
    }

    if (isAuthor) {
        card.querySelector('[data-action="delete"]').addEventListener('click', async () => {
            if (!confirm('Supprimer cette publication du groupe ?')) return;
            try {
                await deleteGroupPost(groupId, post.id);
                card.style.transition = 'opacity 0.3s';
                card.style.opacity = '0';
                setTimeout(() => card.remove(), 300);
            } catch (err) {
                alert('Erreur lors de la suppression.');
            }
        });
    }

    return card;
}

let groupVerseObserverInstance = null;
function createGroupVerseObserver() {
    if (!groupVerseObserverInstance) {
        groupVerseObserverInstance = new IntersectionObserver(async (entries) => {
            for (const entry of entries) {
                if (!entry.isIntersecting) continue;
                const container = entry.target;
                if (container.dataset.loaded === 'true') continue;
                groupVerseObserverInstance.unobserve(container);

                const { book, chapter, verseStart, verseEnd } = container.dataset;
                if (!book || !chapter) continue;
                try {
                    const version = localStorage.getItem('user_preferred_lang') || 'french';
                    let text = '';
                    if (verseStart && (!verseEnd || verseEnd === verseStart)) {
                        text = await getVerse(version, book, parseInt(chapter), parseInt(verseStart));
                    } else if (verseStart && verseEnd) {
                        const verses = await getVerses(version, book, parseInt(chapter), parseInt(verseStart), parseInt(verseEnd));
                        text = verses.map(v => v.text).join(' ');
                    }
                    if (text) {
                        const node = document.createElement('div');
                        node.className = 'verse-text-inline';
                        node.innerHTML = `<div class="verse-text-ref">${escapeHtml(text)}</div>`;
                        container.appendChild(node);
                    }
                } catch { /* silently fail */ }
                container.dataset.loaded = 'true';
            }
        }, { rootMargin: '100px' });
    }
    return groupVerseObserverInstance;
}

// ─────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────

function cleanupGroupFeed() {
    if (groupFeedUnsubscribe) {
        groupFeedUnsubscribe();
        groupFeedUnsubscribe = null;
    }
    groupVerseObserverInstance = null;
}

function showGroupToast(message, type = 'success') {
    const existing = document.querySelector('.share-toast');
    if (existing) existing.remove();
    const toast = document.createElement('div');
    toast.className = `share-toast share-toast--${type}`;
    toast.textContent = message;
    document.body.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('share-toast--visible'));
    setTimeout(() => {
        toast.classList.remove('share-toast--visible');
        setTimeout(() => toast.remove(), 300);
    }, 3000);
}

function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}
